import axios from 'axios';
import oauthConfig from '../../../config/oauth.js';
const OAUTH_API_URL = 'https://api-id-dev.minimarket116.kz/api/v1';
const API_URL = 'https://api-pos.minimarket116.kz/api/v1';

// Создание экземпляра axios с базовым URL
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Экземпляр axios для OAuth
const oauthApi = axios.create({
    baseURL: OAUTH_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Генерация случайной строки для использования в качестве challenge
const generateRandomString = (length) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = new Uint8Array(length);
    window.crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
        result += charset[values[i] % charset.length];
    }
    return result;
};

// Кодирование строки в Base64 URL-safe формат
const base64UrlEncode = (str) => {
    return btoa(str)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

// Генерация code challenge из code verifier
const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(String.fromCharCode(...new Uint8Array(digest)));
};

// Функции для работы с токенами
const tokenManager = {
    // Получение access token из localStorage
    getAccessToken: () => localStorage.getItem('access_token'),

    // Получение refresh token из localStorage
    getRefreshToken: () => localStorage.getItem('refresh_token'),

    // Получение времени истечения токена
    getTokenExpiration: () => {
        const expiresAt = localStorage.getItem('access_token_expires_at');
        return expiresAt ? parseInt(expiresAt, 10) : null;
    },

    // Установка токенов
    setTokens: (accessToken, refreshToken, expiresIn) => {
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        // Сохраняем время истечения токена, если передан expiresIn
        if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000; // Переводим секунды в миллисекунды и добавляем к текущему времени
            localStorage.setItem('access_token_expires_at', expiresAt.toString());
        }
    },

    // Очистка токенов
    clearTokens: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token_expires_at');
        localStorage.removeItem('user');
        localStorage.removeItem('code_verifier');
        localStorage.removeItem('state');
    },

    // Проверка, истечет ли токен в течение указанного времени (в миллисекундах)
    willTokenExpireSoon: (timeThreshold = 5 * 60 * 1000) => { // По умолчанию 5 минут
        const expiresAt = tokenManager.getTokenExpiration();
        if (!expiresAt) return true; // Если нет данных о времени истечения, считаем, что токен скоро истечет

        const timeUntilExpiration = expiresAt - Date.now();
        return timeUntilExpiration <= timeThreshold;
    }
};

// Флаг для отслеживания выполнения рефреша токена
let isRefreshing = false;
// Очередь запросов, ожидающих обновления токена
let failedQueue = [];

// Функция для обработки очереди запросов
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Обновление токена
const refreshAuthToken = async () => {
    try {
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
            throw new Error('Refresh token not available');
        }

        const response = await axios.post(`${OAUTH_API_URL}/oauth/token`,
            new URLSearchParams({
                'grant_type': 'refresh_token',
                'client_id': oauthConfig.clientId,
                'refresh_token': refreshToken
            }),
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }
        );

        if (response.data.access_token) {
            const { access_token, refresh_token, expires_in } = response.data;
            tokenManager.setTokens(access_token, refresh_token, expires_in);
            return access_token;
        } else {
            throw new Error('Failed to refresh token');
        }
    } catch (error) {
        console.error('Ошибка при обновлении токена:', error);
        throw error; // Просто пробрасываем ошибку дальше без очистки токенов
    }
};

// Интерцептор для добавления токена авторизации к oauthApi
oauthApi.interceptors.request.use(
    (config) => {
        const accessToken = tokenManager.getAccessToken();
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Интерцептор для добавления токена авторизации к основному api
api.interceptors.request.use(
    (config) => {
        const accessToken = tokenManager.getAccessToken();
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Интерцептор для обработки ответов oauthApi
oauthApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если ошибка не 401 или это уже повторный запрос после рефреша, просто отклоняем
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Если уже идет процесс обновления токена, добавляем запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject});
            })
                .then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return oauthApi(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        // Начинаем процесс обновления токена
        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshAuthToken();
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            isRefreshing = false;
            return oauthApi(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;
            return Promise.reject(refreshError);
        }
    }
);

// Интерцептор для обработки ответов основного api
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если ошибка не 401 или это уже повторный запрос после рефреша, просто отклоняем
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Если уже идет процесс обновления токена, добавляем запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject});
            })
                .then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return api(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        // Начинаем процесс обновления токена
        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshAuthToken();
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            isRefreshing = false;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;

            // Если не удалось обновить токен (возможно, refresh token истек),
            // только тогда очищаем токены и перенаправляем на страницу логина
            tokenManager.clearTokens();
            window.location.href = '/login';

            return Promise.reject(refreshError);
        }
    }
);

// Сервис для работы с кассами (cash registers)
export const cashRegisterService = {
    // Получить список касс с пагинацией и поиском
    getCashRegisters: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/cash-registers?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка касс');
        }
    },

    // Получить данные кассы по ID
    getCashRegisterById: async (id) => {
        try {
            const response = await api.get(`/cash-registers/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных кассы';
            throw error;
        }
    },

    // Получить данные для формы кассы (списки, справочники)
    getFormData: async () => {
        try {
            const response = await api.get('/cash-registers/form-data');
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных';
            throw error;
        }
    },

    // Сохранить данные кассы
    saveCashRegister: async (id, data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/cash-registers/${id}`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при сохранении данных кассы');
        }
    },

    // Создать новую кассу
    createCashRegister: async (data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post(`/cash-registers`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при создании кассы');
        }
    },

    // Получение истории изменений кассы
    getCashRegisterHistory: async (cashRegisterId) => {
        try {
            const response = await api.get(`/cash-registers/${cashRegisterId}/history`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении истории изменений');
        }
    },

    // Разлогинить все устройства для кассы
    logoutAllDevices: async (cashRegisterId, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/cash-registers/logout-all-device',
                { cash_register_id: cashRegisterId },
                { headers }
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при разлогинивании устройств');
        }
    },

    // Авторизовать устройство для кассы
    authDevice: async (cashRegisterId, authCode, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/cash-registers/auth-device',
                { cash_register_id: cashRegisterId, auth_code: authCode },
                { headers }
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при авторизации устройства');
        }
    },

    // Получить список cash tracking с пагинацией и поиском
    getCashTrackingSessions: async (id, params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/cash-registers/cash-tracking/${id}?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка касс');
        }
    },

    // Получить данные кассы по ID
    getCashTrackingSessionById: async (id) => {
        try {
            const response = await api.get(`/cash-registers/cash-tracking/session/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных кассы';
            throw error;
        }
    },

    // Поиск товаров по имени для быстрых товаров
    getProductsByName: async (name) => {
        try {
            const response = await api.get(`/cash-registers/quick-products?search=${name}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных товаров';
            throw error;
        }
    },
};

// Сервис для работы с кассирами (cashiers)
export const cashierService = {
    // Получить список кассиров с пагинацией и поиском
    getCashiers: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/cashiers?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка кассиров');
        }
    },

    // Получить данные кассира по ID
    getCashierById: async (id) => {
        try {
            const response = await api.get(`/cashiers/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных кассира';
            throw error;
        }
    },

    // Получить данные для формы кассира (списки, справочники)
    getFormData: async () => {
        try {
            const response = await api.get('/cashiers/form-data');
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных';
            throw error;
        }
    },

    // Сохранить данные кассира
    saveCashier: async (id, data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/cashiers/${id}`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при сохранении данных кассира');
        }
    },

    // Создать нового кассира
    createCashier: async (data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post(`/cashiers`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при создании кассира');
        }
    },

    // Получение истории изменений кассира
    getCashierHistory: async (cashierId) => {
        try {
            const response = await api.get(`/cashiers/${cashierId}/history`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении истории изменений');
        }
    }
};

// Сервис для работы с кассирами (cashiers)
export const customerService = {
    // Получить список кассиров с пагинацией и поиском
    getCustomers: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/customers?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка клиентов');
        }
    },

    // Получить данные кассира по ID
    getCustomerById: async (id) => {
        try {
            const response = await api.get(`/customers/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных клиента';
            throw error;
        }
    },

    // Сохранить данные кассира
    saveCustomer: async (id, data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/customers/${id}`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при сохранении данных клиента');
        }
    },

    // Создать нового кассира
    createCustomer: async (data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post(`/customers`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при создании клиента');
        }
    },
};

// Сервис для работы с фискальными кассами (fiscal_cash_registers)
export const fiscalCashRegisterService = {
    // Получить список фискальных касс с пагинацией и поиском
    getFiscalCashRegisters: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/fiscal-cash-registers?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка фискальных касс');
        }
    },

    // Получить данные фискальной кассы по ID
    getFiscalCashRegisterById: async (id) => {
        try {
            const response = await api.get(`/fiscal-cash-registers/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных фискальной кассы';
            throw error;
        }
    },

    // Получить данные для формы фискальной кассы (списки, справочники)
    getFormData: async () => {
        try {
            const response = await api.get('/fiscal-cash-registers/form-data');
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных';
            throw error;
        }
    },

    // Сохранить данные фискальной кассы
    saveFiscalCashRegister: async (id, data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/fiscal-cash-registers/${id}`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при сохранении данных фискальной кассы');
        }
    },

    // Создать новую фискальную кассу
    createFiscalCashRegister: async (data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post(`/fiscal-cash-registers`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при создании фискальной кассы');
        }
    },

    // Получение истории изменений фискальной кассы
    getFiscalCashRegisterHistory: async (fiscalCashRegisterId) => {
        try {
            const response = await api.get(`/fiscal-cash-registers/${fiscalCashRegisterId}/history`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении истории изменений');
        }
    }
};

// Сервис для работы со способами оплаты (payment_methods)
export const paymentMethodService = {
    // Получить список способов оплаты с пагинацией и поиском
    getPaymentMethods: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/payment-methods?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка способов оплаты');
        }
    },

    // Получить данные способа оплаты по ID
    getPaymentMethodById: async (id) => {
        try {
            const response = await api.get(`/payment-methods/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных способа оплаты';
            throw error;
        }
    },

    // Получить данные для формы способа оплаты (списки, справочники)
    getFormData: async () => {
        try {
            const response = await api.get('/payment-methods/form-data');
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных';
            throw error;
        }
    },

    // Сохранить данные способа оплаты
    savePaymentMethod: async (id, data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/payment-methods/${id}`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при сохранении данных способа оплаты');
        }
    },

    // Создать новый способ оплаты
    createPaymentMethod: async (data, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post(`/payment-methods`, data, { headers });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при создании способа оплаты');
        }
    },

    // Получение истории изменений способа оплаты
    getPaymentMethodHistory: async (paymentMethodId) => {
        try {
            const response = await api.get(`/payment-methods/${paymentMethodId}/history`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении истории изменений');
        }
    }
};

// Сервис для работы с продажами
export const salesService = {

    // Получение чека по ID
    getReceipt: async (receiptId) => {
        try {
            const response = await api.get(`/sales/receipt/${receiptId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении чека'
            };
        }
    },

    // Получить список продаж с пагинацией и поиском
    getSales: async (params = {}) => {
        try {
            const { limit = 50, cursor = null, search = '', filters = null } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }
            if (filters) {
                queryParams.append('filters', filters);
            }

            const response = await api.get(`/sales?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка продаж');
        }
    },

    // Получить данные продажи по ID
    getSaleById: async (id) => {
        try {
            const response = await api.get(`/sales/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных продажи';
            throw error;
        }
    },

    // Получение prepare по ID
    getReturnPrepare: async (saleId) => {
        try {
            const response = await api.get(`/sales/returns/prepare/${saleId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении данных возврата '
            };
        }
    },

    // Получение prepare по ID
    getRefundPrepare: async (saleId) => {
        try {
            const response = await api.get(`/sales/refund/prepare/${saleId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении данных'
            };
        }
    },

    // Получение prepare по ID
    getChargePrepare: async (saleId) => {
        try {
            const response = await api.get(`/sales/charge/prepare/${saleId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении данных'
            };
        }
    },

    // Edit note
    editNote: async (saleId, noteData, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.put(`/sales/note-edit/${saleId}`, noteData, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании возврата');
        }
    },

    // Создание return
    createReturn: async (returnData, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/sales/returns', returnData, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании возврата');
        }
    },

    // Создание refund
    createRefund: async (refundData, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/sales/refund', refundData, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании возврата');
        }
    },

    // Создание charge
    createCharge: async (chargeData, correlationKey, idempotencyKey) => {
        try {
            const headers = {};
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/sales/charge', chargeData, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании начисления');
        }
    },

    // Экспорт продаж в CSV
    exportSalesCSV: async (params = {}) => {
        try {
            const { date_from, date_to, platform } = params;
            const queryParams = new URLSearchParams();

            if (date_from) queryParams.append('date_from', date_from);
            if (date_to) queryParams.append('date_to', date_to);
            if (platform) queryParams.append('platform', platform);

            const response = await api.get(`/sales/export/csv?${queryParams.toString()}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при экспорте продаж'
            };
        }
    },

    // Экспорт возвратов в CSV
    exportReturnsCSV: async (params = {}) => {
        try {
            const { date_from, date_to, platform } = params;
            const queryParams = new URLSearchParams();

            if (date_from) queryParams.append('date_from', date_from);
            if (date_to) queryParams.append('date_to', date_to);
            if (platform) queryParams.append('platform', platform);

            const response = await api.get(`/sales/export/returns/csv?${queryParams.toString()}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при экспорте возвратов'
            };
        }
    },

    // Экспорт транзакций в CSV
    exportTransactionsCSV: async (params = {}) => {
        try {
            const { date_from, date_to } = params;
            const queryParams = new URLSearchParams();

            if (date_from) queryParams.append('date_from', date_from);
            if (date_to) queryParams.append('date_to', date_to);

            const response = await api.get(`/sales/export/transactions/csv?${queryParams.toString()}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при экспорте транзакций'
            };
        }
    },
};

// OAuth сервис
export const oauthService = {
    // Инициализация OAuth процесса
    initOAuth: async () => {
        // Генерируем code_verifier и state
        const codeVerifier = generateRandomString(64);
        const state = generateRandomString(32);

        // Сохраняем их в localStorage
        localStorage.setItem('code_verifier', codeVerifier);
        localStorage.setItem('state', state);

        // Генерируем code_challenge
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        // Формируем URL для перенаправления
        const authUrl = `https://${oauthConfig.domain}/oauth/authorize?` +
            `client_id=${encodeURIComponent(oauthConfig.clientId)}` +
            `&redirect_uri=${encodeURIComponent(oauthConfig.redirectUri)}` +
            `&response_type=${encodeURIComponent(oauthConfig.responseType)}` +
            `&scope=${encodeURIComponent(oauthConfig.scope)}` +
            `&state=${encodeURIComponent(state)}` +
            `&code_challenge=${encodeURIComponent(codeChallenge)}` +
            `&code_challenge_method=${encodeURIComponent(oauthConfig.codeChallengeMethod)}`;

        return authUrl;
    },

    // Обработка callback и получение токена
    handleCallback: async (code, receivedState) => {
        // Проверяем, обрабатывали ли мы уже этот код авторизации
        const processedCode = sessionStorage.getItem('processed_auth_code');
        if (processedCode === code) {
            // Если код уже был обработан, возвращаем информацию о текущем пользователе
            const user = oauthService.getCurrentUser();
            return user ? { success: true, user } : { success: false, message: 'Сессия авторизации уже завершена' };
        }

        const savedState = localStorage.getItem('state');
        const codeVerifier = localStorage.getItem('code_verifier');

        // Проверяем state
        if (!savedState || savedState !== receivedState) {
            return { success: false, message: 'Ошибка безопасности: параметр state не совпадает' };
        }

        if (!codeVerifier) {
            return { success: false, message: 'Ошибка: отсутствует code_verifier' };
        }

        try {
            // Помечаем код как обрабатываемый
            sessionStorage.setItem('processed_auth_code', code);

            // Запрос на получение токенов
            const tokenResponse = await axios.post(`${OAUTH_API_URL}/oauth/token`,
                new URLSearchParams({
                    'grant_type': 'authorization_code',
                    'client_id': oauthConfig.clientId,
                    'code': code,
                    'redirect_uri': oauthConfig.redirectUri,
                    'code_verifier': codeVerifier
                }),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }
            );

            if (tokenResponse.data.access_token) {
                const { access_token, refresh_token, expires_in } = tokenResponse.data;
                tokenManager.setTokens(access_token, refresh_token, expires_in);

                // Получаем информацию о пользователе
                const userInfoResponse = await axios.get(`${OAUTH_API_URL}/oauth/user-info`, {
                    headers: {
                        'Authorization': `Bearer ${access_token}`
                    }
                });

                if (userInfoResponse.data.status === 'success') {
                    // Сохраняем информацию о пользователе
                    localStorage.setItem('user', JSON.stringify(userInfoResponse.data));

                    // Очищаем временные данные OAuth
                    localStorage.removeItem('code_verifier');
                    localStorage.removeItem('state');

                    return { success: true, user: userInfoResponse.data };
                } else {
                    throw new Error('Не удалось получить информацию о пользователе');
                }
            } else {
                throw new Error('Не удалось получить токен доступа');
            }
        } catch (error) {
            // В случае ошибки очищаем информацию о коде, чтобы можно было повторить попытку
            sessionStorage.removeItem('processed_auth_code');
            console.error('Ошибка при обработке OAuth callback:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при аутентификации'
            };
        }
    },

    // Отзыв токена
    revokeToken: async () => {
        try {
            const refreshToken = tokenManager.getRefreshToken();
            if (!refreshToken) {
                return { success: false, message: 'Отсутствует refresh_token' };
            }

            const response = await axios.post(`${OAUTH_API_URL}/oauth/revoke`,
                new URLSearchParams({
                    'client_id': oauthConfig.clientId,
                    'token': refreshToken,
                    'token_type_hint': 'refresh_token'
                }),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }
            );

            return { success: response.data.status === 'success', message: response.data.message };
        } catch (error) {
            console.error('Ошибка при отзыве токена:', error);
            return { success: false, message: error.response?.data?.message || 'Ошибка при отзыве токена' };
        }
    },

    // Выход из системы с отзывом токена
    logout: async (navigate) => {
        try {
            // Отзываем токен перед очисткой данных
            await oauthService.revokeToken();

            // Очищаем токены независимо от результата отзыва
            tokenManager.clearTokens();

            // Перенаправляем пользователя
            if (navigate) {
                navigate('/login');
            } else {
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Ошибка при выходе:', error);

            // В случае ошибки все равно очищаем токены и перенаправляем
            tokenManager.clearTokens();

            if (navigate) {
                navigate('/login');
            } else {
                window.location.href = '/login';
            }
        }
    },

    // Проверка аутентификации
    isAuthenticated: () => {
        return !!tokenManager.getAccessToken() && !!tokenManager.getRefreshToken();
    },

    // Получение текущего пользователя
    getCurrentUser: () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            return JSON.parse(userStr);
        }
        return null;
    },

    // Проверка разрешений
    hasPermission: (permission) => {
        const user = oauthService.getCurrentUser();
        if (!user || !user.permissions) {
            return false;
        }
        return user.permissions.includes(permission);
    },

    // Проверка нескольких разрешений (ИЛИ)
    hasAnyPermission: (permissions) => {
        const user = oauthService.getCurrentUser();
        if (!user || !user.permissions) {
            return false;
        }
        return permissions.some(permission => user.permissions.includes(permission));
    },

    // Проверка всех разрешений (И)
    hasAllPermissions: (permissions) => {
        const user = oauthService.getCurrentUser();
        if (!user || !user.permissions) {
            return false;
        }
        return permissions.every(permission => user.permissions.includes(permission));
    },
};

export const authService = {
    isAuthenticated: oauthService.isAuthenticated,
    getCurrentUser: oauthService.getCurrentUser,
    hasPermission: oauthService.hasPermission,
    hasAnyPermission: oauthService.hasAnyPermission,
    hasAllPermissions: oauthService.hasAllPermissions,
    logout: oauthService.logout,
    hasTemporaryPassword: () => false // Эта функция больше не используется, но оставляем для совместимости
};
export default {
    oauthApi,
    api
};